import { useMemo, useState, useEffect } from "react"
import { GanttChart, type GanttRow } from "./gantt-chart"
import type { EngagementHoursRow } from "@/types/report"
import { useReportData } from "@/hooks/use-report-data"
import { min as minDate, max as maxDate } from "date-fns"
import { Skeleton } from "@/components/ui/skeleton"

function billingTypeToColor(billingType: string | null): string {
  switch (billingType) {
    case "Hourly":
    case "TimeBased": return "bg-blue-500"
    case "Milestone": return "bg-emerald-500"
    case "Fixed Fee": return "bg-violet-500"
    default: return "bg-slate-400"
  }
}

const EMPTY_FILTERS = {} as const

interface PeopleGanttProps {
  workerIdFilter?: Set<number> | null
}

export function PeopleGantt({ workerIdFilter }: PeopleGanttProps = {}) {
  // Fetch hours data which has per-worker-per-engagement rows with dates
  const { data: hoursData, isLoading, error } = useReportData("engagement-hours", EMPTY_FILTERS)

  const rows = useMemo<GanttRow[]>(() => {
    if (!hoursData || hoursData.length === 0) return []

    // Group by worker
    const workerMap = new Map<number, {
      name: string
      type: string | null
      engagements: {
        code: string | null
        startDate: Date
        endDate: Date
        billingType: string | null
      }[]
    }>()

    for (const row of hoursData as EngagementHoursRow[]) {
      if (!row.engagementStartDate || !row.engagementEndDate) continue
      if (workerIdFilter && !workerIdFilter.has(row.userProfileId)) continue

      const existing = workerMap.get(row.userProfileId)
      const engagement = {
        code: row.engagementCode,
        startDate: new Date(row.engagementStartDate),
        endDate: new Date(row.engagementEndDate),
        billingType: row.billingType,
      }

      if (existing) {
        existing.engagements.push(engagement)
      } else {
        workerMap.set(row.userProfileId, {
          name: row.workerName ?? "Unknown",
          type: row.workerType,
          engagements: [engagement],
        })
      }
    }

    // Convert to GanttRow[] with children
    return Array.from(workerMap.entries()).map(([profileId, worker]) => {
      const allStarts = worker.engagements.map(e => e.startDate)
      const allEnds = worker.engagements.map(e => e.endDate)

      return {
        id: profileId,
        label: worker.name,
        sublabel: worker.type ?? undefined,
        startDate: minDate(allStarts),
        endDate: maxDate(allEnds),
        color: "bg-slate-500",
        children: worker.engagements.map((eng, i) => ({
          id: `${profileId}-${eng.code ?? i}`,
          label: eng.code ?? "—",
          startDate: eng.startDate,
          endDate: eng.endDate,
          color: billingTypeToColor(eng.billingType),
        })),
      }
    })
  }, [hoursData, workerIdFilter])

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-red-600">
        Failed to load engagement data for Gantt view.
      </div>
    )
  }

  return <GanttChart rows={rows} expandable />
}
